import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import Button from '../../components/Button';
import Form, { Field } from '../../components/Form';
import FormField from '../../components/formInternals/FormField';
import IconButton from '../../components/IconButton';
import Modal from '../../components/Modal';
import Tooltip from '../../components/Tooltip';
import { UserType } from '../../graphql/generated/graphqlTypes';
import closeIcon from '../../icons/clear.svg';
import useQueryStringState from '../../utils/useQueryStringState';
import styles from './TeammatesFilter.module.scss';
import TeammateTypeLabel from './TeammateTypeLabel';

export type TeammatesFilterType = 'ADMIN' | 'GUEST' | 'USER';
export type TeammatesFilterStatus =
  | 'ACTIVE'
  | 'DEACTIVATED'
  | 'INVITED'
  | 'PENDING'
  | 'REJECTED';

const BILLABLE_TYPES: TeammatesFilterType[] = ['ADMIN', 'USER'];
const BILLABLE_STATUSES: TeammatesFilterStatus[] = ['ACTIVE'];

type FormValues = {
  types: TeammatesFilterType[];
  statuses: TeammatesFilterStatus[];
};

interface LabelWithTooltipProps {
  label: string;
  tooltip: string;
}
const LabelWithTooltip = ({ label, tooltip }: LabelWithTooltipProps) => (
  <Tooltip trigger="hover" placement="top" tooltip={tooltip}>
    <span>{label}</span>
  </Tooltip>
);

interface FilterObserverProps {
  onChangeTypes: (types: TeammatesFilterType[]) => void;
  onChangeStatuses: (types: TeammatesFilterStatus[]) => void;
}

const FilterObserver = ({
  onChangeTypes,
  onChangeStatuses,
}: FilterObserverProps) => {
  const { values } = useFormikContext<FormValues>();

  useEffect(() => {
    onChangeTypes(values.types);
  }, [onChangeTypes, values.types]);

  useEffect(() => {
    onChangeStatuses(values.statuses);
  }, [onChangeStatuses, values.statuses]);

  return null;
};

interface OnlyBillableProps {
  types: TeammatesFilterType[];
  statuses: TeammatesFilterStatus[];
  onSelectOnlyBillable: () => void;
}
const OnlyBillable = ({
  types,
  statuses,
  onSelectOnlyBillable,
}: OnlyBillableProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [onlyBillable, setOnlyBillable] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const onlyBillableAreSelected =
    types.length === BILLABLE_TYPES.length &&
    statuses.length === BILLABLE_STATUSES.length &&
    BILLABLE_TYPES.every((billableType) => types.includes(billableType)) &&
    BILLABLE_STATUSES.every((billableStatus) =>
      statuses.includes(billableStatus),
    );

  useEffect(() => {
    if (!onlyBillableAreSelected) {
      setOnlyBillable(false);
    }
  }, [onlyBillableAreSelected]);

  return (
    <FormField
      label="Only billable users"
      type="checkbox"
      name="onlyBillable"
      checked={onlyBillable}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
        const { checked } = event.target;
        setOnlyBillable(checked);
        if (checked) {
          onSelectOnlyBillable();
        }
      }}
    />
  );
};

export const DEFAULT_TEAMMATES_TYPE: TeammatesFilterType[] = [];
export const DEFAULT_TEAMMATES_STATUS: TeammatesFilterStatus[] = ['ACTIVE'];

interface TeammatesFilterProps {
  asModal?: boolean;
  close?: () => void;
  filteredTeammates: number;
  requireInviteApproval: boolean;
}

const TeammatesFilter = ({
  asModal = false,
  close,
  filteredTeammates,
  requireInviteApproval,
}: TeammatesFilterProps) => {
  const [types, onChangeTypes] = useQueryStringState<TeammatesFilterType[]>(
    'types',
    DEFAULT_TEAMMATES_TYPE,
  );
  const [statuses, onChangeStatuses] = useQueryStringState<
    TeammatesFilterStatus[]
  >('statuses', DEFAULT_TEAMMATES_STATUS);
  const inner = (
    <Form<FormValues> initialValues={{ types, statuses }} enableReinitialize>
      {({ values, setValues }) => (
        <>
          <FilterObserver
            onChangeTypes={onChangeTypes}
            onChangeStatuses={onChangeStatuses}
          />
          <div className={styles.filter}>
            <div className={styles.sections}>
              <div className={styles.section}>
                <div className={styles.sectionHeading}>Account type</div>
                <Field
                  label={<TeammateTypeLabel type={UserType.User} />}
                  type="checkbox"
                  name="types"
                  value="USER"
                />
                <Field
                  label={<TeammateTypeLabel type={UserType.Admin} />}
                  type="checkbox"
                  name="types"
                  value="ADMIN"
                />
                <Field
                  label={<TeammateTypeLabel type={UserType.Guest} />}
                  type="checkbox"
                  name="types"
                  value="GUEST"
                />
              </div>
              <div className={styles.section}>
                <div className={styles.sectionHeading}>Status</div>

                <Field
                  label={
                    <LabelWithTooltip
                      label="Active"
                      tooltip="All users who can log in"
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="ACTIVE"
                />
                <Field
                  label={
                    <LabelWithTooltip
                      label="Deactivated"
                      tooltip="Former users who can no longer log in"
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="DEACTIVATED"
                />
                <Field
                  label={
                    <LabelWithTooltip
                      label="Invited"
                      tooltip="Invitations waiting for users to accept"
                    />
                  }
                  type="checkbox"
                  name="statuses"
                  value="INVITED"
                />
                {requireInviteApproval && (
                  <>
                    <Field
                      label={
                        <LabelWithTooltip
                          label="Pending"
                          tooltip="Invitations waiting for admin approval"
                        />
                      }
                      type="checkbox"
                      name="statuses"
                      value="PENDING"
                    />
                    <Field
                      label={
                        <LabelWithTooltip
                          label="Rejected"
                          tooltip=" Invitation requests that have been rejected by an admin"
                        />
                      }
                      type="checkbox"
                      name="statuses"
                      value="REJECTED"
                    />
                  </>
                )}
              </div>
            </div>
            <div className={styles.bottomSection}>
              <OnlyBillable
                types={values.types}
                statuses={values.statuses}
                onSelectOnlyBillable={() => {
                  setValues({
                    types: BILLABLE_TYPES,
                    statuses: BILLABLE_STATUSES,
                  });
                }}
              />
            </div>
          </div>
        </>
      )}
    </Form>
  );

  return asModal ? (
    <Modal noPadding fullscreen>
      <div className={styles.modalContent}>
        <div>
          <div className={styles.modalTitle}>
            <h4>Filter</h4>
            <div className={styles.closeContainer}>
              <IconButton
                onClick={close}
                applyNegativeMargin
                aria-label="Close"
              >
                {closeIcon}
              </IconButton>
            </div>
          </div>
          {inner}
        </div>
        <div className={styles.modalButton}>
          <Button variant="primary" block onClick={close}>
            Apply ({filteredTeammates} teammates)
          </Button>
        </div>
      </div>
    </Modal>
  ) : (
    inner
  );
};

export default TeammatesFilter;
